import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { useLang } from '../LangContext';
import { AccessDenied, canAccess, canSeeMoney, isOrderInScope } from '../adminAccess';
import DatabaseError from '../components/DatabaseError';
import { orderLocation } from '../orderLocation';
import { adminProofMedia } from '../orderServiceUtils';
import { statusLabel } from '../orderAdminUtils';
import {
  OrderActivityPanel, OrderClientReportPanel, OrderFactsPanel,
  OrderLocationPanel, OrderProofPanel, OrderReviewPanel, OrderServicePanel,
} from './OrdersPage';

const TABS = [
  { key: 'pending_admin_review', label: 'Pending Review', tone: 'amber' },
  { key: 'changes_requested', label: 'Changes Requested', tone: 'red' },
  { key: 'completed', label: 'Approved / Completed', tone: 'green' },
];

const TONE = {
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  green: 'bg-green-100 text-green-700',
};

const stamp = value => {
  if (!value) return '—';
  if (typeof value === 'string') return new Date(value).toLocaleString();
  if (value.toDate) return value.toDate().toLocaleString();
  return '—';
};

/**
 * Work Reviews — a focused queue of submitted jobs awaiting an admin decision.
 *
 * This is NOT a second order system: it reads the same `orders` collection and
 * renders the same panels as Admin -> Orders, which remains the source of
 * truth for every order.
 */
export default function WorkReviewsPage({ user }) {
  const { tr, lang } = useLang();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('pending_admin_review');
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const showMoney = canSeeMoney(user);

  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setError('');
    }, err => setError(err.message));
    return () => unsub();
  }, []);

  const reviewable = useMemo(
    () => orders
      .filter(o => TABS.some(t => t.key === o.status))
      .filter(o => isOrderInScope(user, o)),
    [orders, user],
  );

  const counts = useMemo(() => {
    const map = {};
    for (const t of TABS) map[t.key] = reviewable.filter(o => o.status === t.key).length;
    return map;
  }, [reviewable]);

  const rows = reviewable.filter(o => o.status === tab);

  // Keep the open detail in sync with live updates (e.g. after approving).
  const current = selected ? orders.find(o => o.id === selected.id) || selected : null;

  if (!canAccess(user, 'workReviews')) return <AccessDenied title="Work Reviews" />;

  return (
    <div className="page-shell">
      <div className="mb-6">
        <h1 className="page-title">{tr('workReviews')}</h1>
        <p className="page-description">
          Review worker submissions before marking jobs complete. Every order
          also remains visible in Orders.
        </p>
      </div>

      {error && <DatabaseError message={error} />}

      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
              tab === t.key ? 'bg-primary text-white' : `${TONE[t.tone]} hover:opacity-80`
            }`}>
            {t.label}{counts[t.key] ? ` (${counts[t.key]})` : ''}
          </button>
        ))}
      </div>

      {rows.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <p className="text-gray-400 text-sm">Nothing in this queue.</p>
        </div>
      )}

      <div className="grid gap-3">
        {rows.map(o => {
          // Badges describe ADMIN proof, never the customer's entitlement.
          // Photos AND a video are required on every package, so neither
          // badge is ever "n/a".
          const { before, after, video } = adminProofMedia(o);
          const loc = orderLocation(o);

          const chip = (ok, label) => (ok
            ? <span className="badge bg-green-100 text-green-700">{label}: ✓</span>
            : <span className="badge bg-red-100 text-red-700">{label}: missing</span>);

          return (
            <div key={o.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-gray-400">{o.orderNumber || o.id}</p>
                  <p className="font-semibold text-gray-900">
                    {o.customerName || o.customerEmail || o.customerId || '—'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {o.packageName || '—'} · {loc.city} · {loc.cemetery}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Worker: {o.workerName || o.workerId || 'unassigned'} · submitted {stamp(o.workerSubmittedAt)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`badge ${TONE[TABS.find(t => t.key === o.status)?.tone || 'amber']}`}>
                    {statusLabel(o.status, lang)}
                  </span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {chip(before.length >= 2 && after.length >= 2, 'Admin photos')}
                    {chip(video.length > 0, 'Admin video')}
                  </div>
                  <button onClick={() => setSelected(o)} className="btn-primary text-xs px-3 py-1.5">
                    Review
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {current && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
             onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-6">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">
                Work review · {current.orderNumber || current.id}
              </h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 space-y-1">
              <div className="text-sm text-gray-600">
                <p><span className="text-gray-400">Customer:</span> {current.customerName || current.customerEmail || '—'}</p>
                <p><span className="text-gray-400">Worker:</span> {current.workerName || current.workerId || 'unassigned'}</p>
                <p><span className="text-gray-400">Submitted:</span> {stamp(current.workerSubmittedAt)}</p>
              </div>
              <OrderFactsPanel order={current} />
              <OrderServicePanel order={current} showMoney={showMoney} />
              <OrderLocationPanel order={current} />
              {/* Stage 1 and stage 2 are rendered as separate sections and are
                  never mixed: only ADMIN PROOF is approval evidence. */}
              <OrderClientReportPanel order={current} />
              <OrderProofPanel order={current} label="submitted for approval" />
              <OrderActivityPanel order={current} />
              <OrderReviewPanel order={current} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
